/**
 * Calendar-day helpers for Notion-sourced events (TASK-410).
 *
 * An event stays active/upcoming until the END of its last day — it counts as
 * past only from the following day onward. Comparisons are done on
 * `YYYY-MM-DD` strings instead of Date instants: Notion date values are
 * either plain dates ("2026-08-24") or ISO datetimes carrying the wall-clock
 * offset ("2026-08-24T18:00:00.000+03:00"), so the first 10 characters are
 * always the calendar day as entered in Notion. Parsing with `new Date()`
 * would mix UTC-parsed date-only values with local midnights and flip events
 * to "past" a day early depending on the timezone.
 */

/** Local calendar day of the given moment as `YYYY-MM-DD`. */
export const localYmd = (date: Date = new Date()): string => {
    const pad = (n: number): string => String(n).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** The event's last calendar day (`YYYY-MM-DD`): the range end when present,
 *  otherwise the start day. Null for undated events. */
export const eventLastDayYmd = (
    dateStart: string | null,
    dateEnd: string | null,
): string | null => {
    const raw = dateEnd ?? dateStart
    return raw ? raw.slice(0, 10) : null
}

/** Whether the event is over: its last day is strictly before `todayYmd`.
 *  Undated events are never past. */
export const isEventPast = (
    dateStart: string | null,
    dateEnd: string | null,
    todayYmd: string,
): boolean => {
    const lastDay = eventLastDayYmd(dateStart, dateEnd)
    return lastDay !== null && lastDay < todayYmd
}
